"use client";

import Link from "next/link";
import { useEffect, useState, useSyncExternalStore, type FormEvent } from "react";
import Footer from "@/components/footer";
import TurnstileChallenge from "@/components/turnstile-challenge";
import { buildAuthFlowRedirectUrl } from "@/lib/auth/redirect-target";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

type Mode = "request" | "update";

type ResetPasswordPageClientProps = {
  initialRecovery: boolean;
  initialStatus: string | null;
  initialErrorMessage: string | null;
};

function isRecoveryUrl(url: URL) {
  if (url.searchParams.get("type") === "recovery") {
    return true;
  }

  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  if (!hash) {
    return false;
  }

  const hashParams = new URLSearchParams(hash.startsWith("?") ? hash.slice(1) : hash);
  return hashParams.get("type") === "recovery";
}

function subscribeToLocationChange(onStoreChange: () => void) {
  window.addEventListener("hashchange", onStoreChange);
  window.addEventListener("popstate", onStoreChange);

  return () => {
    window.removeEventListener("hashchange", onStoreChange);
    window.removeEventListener("popstate", onStoreChange);
  };
}

function getRecoverySnapshot() {
  return isRecoveryUrl(new URL(window.location.href));
}

export default function ResetPasswordPageClient({
  initialRecovery,
  initialStatus,
  initialErrorMessage,
}: ResetPasswordPageClientProps) {
  const supabase = getSupabaseBrowserClient();
  const [mode, setMode] = useState<Mode>(initialRecovery ? "update" : "request");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<string | null>(initialStatus);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    initialErrorMessage,
  );
  const [activeAction, setActiveAction] = useState<Mode | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const hasRecoveryHint = useSyncExternalStore(
    subscribeToLocationChange,
    getRecoverySnapshot,
    () => initialRecovery,
  );
  const isUpdateMode = mode === "update" || hasRecoveryHint;
  const displayStatus =
    status ??
    (isUpdateMode && activeAction !== "update" && !errorMessage
      ? "Choose a new password."
      : null);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("update");
        setStatus("Choose a new password.");
        setErrorMessage(null);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  const handleResetRequest = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!captchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("request");
    setErrorMessage(null);
    setStatus(null);

    const redirectTo = buildAuthFlowRedirectUrl(
      "/reset-password?type=recovery",
      "password_recovery",
      window.location.origin,
    );
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
      captchaToken,
    });

    setActiveAction(null);
    setCaptchaToken(null);
    setCaptchaResetKey((current) => current + 1);

    if (error) {
      setErrorMessage(error.message);
      return;
    }

    setStatus("Check your email for a password reset link.");
  };

  const handlePasswordUpdate = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    setActiveAction("update");
    setErrorMessage(null);
    setStatus(null);

    if (!newPassword || !confirmPassword) {
      setActiveAction(null);
      setErrorMessage("Enter and confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setActiveAction(null);
      setErrorMessage("Passwords do not match.");
      return;
    }

    let nextMetadata: Record<string, unknown> = { has_password: true };
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (!userError && userData?.user?.user_metadata) {
      nextMetadata = {
        ...userData.user.user_metadata,
        has_magic_link: userData.user.user_metadata.has_magic_link,
        has_password: true,
      };
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      data: nextMetadata,
    });

    setActiveAction(null);

    if (error) {
      setErrorMessage(error.message);
      return;
    }

    setNewPassword("");
    setConfirmPassword("");
    setStatus("Password updated. You can sign in now.");
  };

  return (
    <div className="min-h-screen text-[var(--text)]">
      <main className="relative min-h-screen">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 pb-24 pt-12 lg:flex-row lg:items-start">
          <section className="flex-1 space-y-6">
            <Link
              href="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/30"
            >
              Return home
            </Link>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.4em] text-[var(--muted)]">
                deepface.dev access
              </p>
              <h1 className="font-[var(--font-display)] text-3xl text-[var(--text)] sm:text-4xl">
                Reset your password
              </h1>
              <p className="max-w-xl text-sm text-[var(--muted)] sm:text-base">
                We will email you a secure link to reset your password and keep
                your account protected.
              </p>
            </div>
            <div className="panel border border-white/10 p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                Remembered it?
              </p>
              <p className="mt-3 text-sm text-[var(--text)]">
                Return to sign in to access your dashboard.
              </p>
              <Link
                href="/signin"
                className="mt-5 inline-flex rounded-full border border-white/20 bg-white/5 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/40"
              >
                Sign in
              </Link>
            </div>
          </section>

          <section className="panel w-full max-w-xl border border-white/10 p-6 sm:p-8">
            <div className="space-y-6">
              {(displayStatus || errorMessage) && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-xs uppercase tracking-[0.3em] ${
                    errorMessage
                      ? "border-red-400/40 text-red-200"
                      : "border-emerald-400/40 text-emerald-200"
                  }`}
                  role={errorMessage ? "alert" : "status"}
                >
                  {errorMessage ?? displayStatus}
                </div>
              )}

              {!isUpdateMode ? (
                <form className="space-y-4" onSubmit={handleResetRequest}>
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    Send reset link
                  </p>
                  <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    Email
                    <input
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={activeAction === "request" || !captchaToken}
                    className="w-full rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {activeAction === "request"
                      ? "Sending link..."
                      : "Send reset link"}
                  </button>
                  <TurnstileChallenge
                    key={captchaResetKey}
                    action="password_reset"
                    onTokenChange={setCaptchaToken}
                  />
                  <p className="text-xs text-[var(--muted)]">
                    Need a new account?{" "}
                    <Link
                      href="/signup"
                      className="text-[var(--accent)] hover:text-[var(--accent-2)]"
                    >
                      Create one
                    </Link>
                    .
                  </p>
                </form>
              ) : (
                <form className="space-y-4" onSubmit={handlePasswordUpdate}>
                  <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    Set a new password
                  </p>
                  <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    New password
                    <input
                      type="password"
                      name="new-password"
                      placeholder="Create a new password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      required
                      className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                    Confirm password
                    <input
                      type="password"
                      name="confirm-password"
                      placeholder="Confirm your new password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                      className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={activeAction === "update"}
                    className="w-full rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {activeAction === "update"
                      ? "Updating password..."
                      : "Update password"}
                  </button>
                  <p className="text-xs text-[var(--muted)]">
                    Once updated, you can{" "}
                    <Link
                      href="/signin"
                      className="text-[var(--accent)] hover:text-[var(--accent-2)]"
                    >
                      sign in
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
